import { env } from '@shared/config/env'
import { useAuthStore } from '@shared/store/auth'
import {
  HouseIcon,
  DocumentIcon,
  UserIcon,
  TagIcon,
  CustomersIcon,
  StaffIdIcon,
  CalendarCheckIcon,
  UtilitiesIcon,
  SettingsIcon,
  PliantIcon
} from '@shared/assets/icons'

import { useLocationStore } from '@shared/store/location'

/**
 * Visibility of a nav item is driven by three independent properties:
 * - `enabled`     — account entitlement, from the server driven `featureConfig`.
 * - `meta.scopes` — the user's permissions.
 * - `rolloutFlag` — rollout gating, resolved through Posthog. Declare a key from the `FeatureFlags` registry
 *   and the item disappears until that flag resolves enabled for the merchant. Nothing else
 *   to wire up: the sidebar resolves every flag and filters the tree. Gate the matching route
 *   with the same key via `meta.rolloutFlag` in `uz-router.js`, or it stays reachable by URL.
 */
export default function () {
  const authStore = useAuthStore()
  const locationStore = useLocationStore()
  const {
    moto: hasMoto,
    ecom: hasEcom,
    paylater: hasPaylater,
    linkpay: hasLinkpay
  } = locationStore.merchantFeatures
  const isPaymentLinksEnabled = Boolean(hasPaylater || hasLinkpay)
  const isUodEcomBatchProcessingEnabled =
    !authStore.isSupport && (hasMoto || hasEcom)
  const hasUnzerCardProduct = authStore.me?.user?.hasUnzerCard || false
  const isProduction = env.nodeEnv === 'production'
  const pliantUrl =
    env.pliantSsoUrl || (isProduction ? 'https://my.unzer.com/' : '')

  return [
    // home
    {
      route: '/home',
      name: 'home',
      icon: HouseIcon,
      enabled: true,
      key: 'nav.main.items.home.title',
      children: [],
      meta: {
        scopes: ['home:analytics:read']
      }
    },
    // orders and transactions
    {
      route: '/orders',
      name: 'orders',
      icon: DocumentIcon,
      enabled: true,
      key: 'nav.main.items.orders.title',
      meta: {
        scopes: [
          'reports_orders:read',
          'orders_transactions:read',
          'settings:payment-link-branding:read',
          'transaction_batches:read'
        ]
      },
      children: [
        {
          route: '/reports_orders',
          name: 'reports_orders',
          enabled: true,
          key: 'nav.main.items.orders.reports_orders.title',
          meta: {
            scopes: ['reports_orders:read']
          }
        },
        {
          route: '/orders_transactions',
          name: 'transactions',
          enabled: true,
          key: 'nav.main.items.orders.orders_transactions.title',
          meta: {
            scopes: ['orders_transactions:read']
          }
        },
        {
          route: '/payment_links',
          name: 'payment_links',
          enabled: isPaymentLinksEnabled,
          key: 'nav.main.items.orders.payment_links.title',
          meta: {
            scopes: ['settings:payment-link-branding:read']
          }
        },
        {
          route: '/batch_processing',
          name: 'batch_processing',
          enabled: isUodEcomBatchProcessingEnabled,
          key: 'nav.main.items.orders.batch_processing.title',
          meta: {
            scopes: ['transaction_batches:read']
          }
        }
      ]
    },
    {
      route: '/account',
      name: 'account',
      icon: UserIcon,
      enabled: true,
      key: 'nav.main.items.account.title',
      meta: {
        scopes: ['account:invoices:read']
      },
      children: [
        {
          route: '/invoices',
          name: 'account-invoices',
          enabled: true,
          key: 'nav.main.items.account.invoices',
          meta: {
            scopes: ['account:invoices:read']
          }
        }
      ]
    },
    // products
    {
      route: '/products',
      name: 'products',
      icon: TagIcon,
      enabled: true,
      key: 'nav.main.items.products.title',
      meta: {
        scopes: [
          'products:read',
          'products:product_groups:read',
          'products:addon_groups:read',
          'products:product_templates:read',
          'products:service_questions:read',
          'products:discounts:read',
          'products:pricebooks:read',
          'products:promotions:read',
          'products:tags:read'
        ]
      },
      children: [
        {
          path: '',
          redirect: '/manager'
        },
        {
          route: '/manager',
          name: 'products-manager',
          key: 'nav.main.items.products.manager',
          enabled: true,
          meta: {
            scopes: ['products:read']
          }
        },
        {
          route: '/product_groups',
          name: 'products-product-groups',
          key: 'nav.main.items.products.product_groups',
          enabled: true,
          meta: {
            scopes: ['products:product_groups:read']
          }
        },
        {
          route: '/addon-groups',
          name: 'products-addon-groups',
          key: 'nav.main.items.products.addon_groups',
          enabled: authStore.isGastro || authStore.isLitePos,
          meta: {
            scopes: ['products:addon_groups:read']
          }
        },
        {
          route: '/product_templates',
          name: 'products-product-templates',
          key: 'nav.main.items.products.product_templates',
          enabled: true,
          meta: {
            scopes: ['products:product_templates:read']
          }
        },
        {
          route: '/product_service_questions',
          name: 'products-service-questions',
          key: 'nav.main.items.products.services',
          enabled: true,
          meta: {
            scopes: ['products:service_questions:read']
          }
        },
        {
          route: '/discounts',
          name: 'products-discounts-groups',
          key: 'nav.main.items.products.discounts',
          enabled: true,
          meta: {
            scopes: ['products:discounts:read']
          }
        },
        {
          route: '/pricebooks',
          name: 'products-pricebooks',
          key: 'nav.main.items.products.pricebooks',
          enabled: true,
          meta: {
            scopes: ['products:pricebooks:read']
          }
        },
        {
          route: '/promotions',
          name: 'products-promotions',
          key: 'nav.main.items.products.promotions',
          enabled: true,
          meta: {
            scopes: ['products:promotions:read']
          }
        },
        {
          route: '/tags',
          name: 'products-tags',
          key: 'nav.main.items.products.tags',
          enabled: true,
          meta: {
            scopes: ['products:tags:read']
          }
        }
      ]
    },
    // customers
    {
      route: '/customers',
      name: 'customers',
      icon: CustomersIcon,
      enabled: true,
      key: 'nav.main.items.customers.title',
      meta: {
        scopes: ['customers:read']
      },
      children: []
    },
    // staff
    {
      route: '/staff',
      name: 'staff',
      icon: StaffIdIcon,
      enabled: true,
      key: 'nav.main.items.staff.title',
      meta: {
        scopes: ['staff:read', 'staff:groups:read', 'staff:shift_plans:read']
      },
      children: [
        {
          route: '/manager',
          name: 'staff',
          enabled: true,
          key: 'nav.main.items.staff.all',
          meta: {
            scopes: ['staff:read']
          }
        },
        {
          route: '/staff_groups',
          name: 'staff-groups',
          enabled: true,
          key: 'nav.main.items.staff.staff-groups',
          meta: {
            scopes: ['staff:groups:read']
          }
        },
        {
          route: '/shift_plan',
          name: 'shift-plan',
          enabled: true,
          key: 'nav.main.items.staff.shift_plan',
          meta: {
            scopes: ['staff:shift_plans:read']
          }
        }
      ]
    },
    // reservations
    {
      route: '/reservations',
      name: 'reservations',
      icon: CalendarCheckIcon,
      enabled: true,
      key: 'nav.main.items.reservations.title',
      meta: {
        scopes: [
          'reservations:calendar:read',
          'reservations:services:read',
          'reservations:service_categories:read'
        ]
      },
      children: [
        {
          route: '/calendar',
          name: 'calendar',
          enabled: true,
          key: 'nav.main.items.reservations.calendar',
          meta: {
            scopes: ['reservations:calendar:read']
          }
        },
        {
          route: '/service_category',
          name: 'service-category',
          enabled: true,
          key: 'nav.main.items.reservations.service_category',
          meta: {
            scopes: ['reservations:service_categories:read']
          }
        },
        {
          route: '/services',
          name: 'services',
          enabled: true,
          key: 'nav.main.items.reservations.services',
          meta: {
            scopes: ['reservations:services:read']
          }
        }
      ]
    },
    // utilities
    {
      route: '/utilities',
      name: 'utilities',
      icon: UtilitiesIcon,
      enabled: true,
      key: 'nav.main.items.utilities.title',
      meta: {
        scopes: ['utilities:export_history:read']
      },
      children: [
        {
          route: '/export_history',
          name: 'export-history',
          enabled: true,
          key: 'nav.main.items.utilities.export_history',
          meta: {
            scopes: ['utilities:export_history:read']
          }
        }
      ]
    },
    // settings
    {
      route: '/settings',
      name: 'settings',
      icon: SettingsIcon,
      enabled: true,
      key: 'nav.main.items.settings.title',
      meta: {
        scopes: ['settings:dashboard:read']
      },
      children: [
        {
          route: '/dashboard',
          name: 'settings-dashboard',
          enabled: true,
          key: 'nav.main.items.settings.general',
          meta: {
            scopes: [
              'settings:dashboard:general:read',
              'settings:dashboard:search_results:read',
              'settings:dashboard:customers:read',
              'settings:dashboard:staff:read',
              'settings:dashboard:products:read'
            ]
          }
        },
        {
          route: '/reservations',
          name: 'settings-reservations',
          enabled: true,
          key: 'nav.main.items.settings.reservations',
          meta: {
            scopes: ['settings:dashboard:reservations:read']
          }
        },
        {
          route: '/iam-users',
          name: 'settings-iam-users',
          enabled: true,
          key: 'nav.main.items.settings.iam-users',
          meta: {
            scopes: ['settings:iam-users:read']
          }
        },
        {
          route: '/iam-api-keys',
          name: 'settings-iam-api-keys',
          enabled: true,
          key: 'nav.main.items.settings.iam-api-keys',
          meta: {
            scopes: ['settings:api_keys:list']
          }
        },
        {
          route: '/payment-link-branding',
          name: 'settings-payment-link-branding',
          enabled: isPaymentLinksEnabled,
          key: 'nav.main.items.settings.payment-link-branding',
          meta: {
            scopes: ['settings:payment-link-branding:settings:read']
          }
        }
      ]
    },
    {
      route: pliantUrl,
      name: 'pliant',
      icon: PliantIcon,
      enabled: hasUnzerCardProduct,
      key: 'Unzer Financial Services',
      external: true,
      target: '_blank',
      separator: true,
      meta: {
        scopes: ['unzer_financial_services:read']
      },
      children: []
    }
  ]
}
